
import { gcm } from "@noble/ciphers/aes.js";
import {
  PY_SECRET_KEY,
  REACT_APP_PAYLOAD_ENC_KEY,
} from "../../ytiruces";


const USE_WEB_API = true;

const NONCE_SIZE = 12;

const b64ToBytes = (b64) =>
  Uint8Array.from(atob(b64.trim()), (c) => c.charCodeAt(0));

const bytesToB64 = (bytes) => {
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin);
};

// ---------- key cache (separate per mode) ----------
const keyCache = {};
const getKey = (name, b64Key) => {
  const id = `${USE_WEB_API ? "web" : "pkg"}:${name}`;
  if (!keyCache[id]) {
    keyCache[id] = USE_WEB_API
      ? crypto.subtle.importKey(
        "raw",
        b64ToBytes(b64Key),
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      )
      : b64ToBytes(b64Key);
  }
  return keyCache[id];
};

// ---------- core encrypt / decrypt ----------
const gcmEncryptRaw = async (key, data) => {
  const text = typeof data === "string" ? data : JSON.stringify(data);
  const plainBytes = new TextEncoder().encode(text);

  if (USE_WEB_API) {
    const nonce = crypto.getRandomValues(new Uint8Array(NONCE_SIZE));
    const encrypted = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: nonce, tagLength: 128 },
        key,
        plainBytes
      )
    );
    return { nonce, encrypted };
  }

  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_SIZE));
  const encrypted = gcm(key, nonce).encrypt(plainBytes);
  return { nonce, encrypted };
};

const gcmDecryptRaw = async (key, iv, ctWithTag) => {
  if (USE_WEB_API) {
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      key,
      ctWithTag
    );
    return new Uint8Array(plain);
  }
  return gcm(key, iv).decrypt(ctWithTag);
};

// ---------- public API ----------
export const encrypt_5second = async (data) => {
  try {
    const k = await getKey("header", REACT_APP_PAYLOAD_ENC_KEY);
    const { nonce, encrypted } = await gcmEncryptRaw(k, data);
    const out = new Uint8Array(NONCE_SIZE + encrypted.length);
    out.set(nonce, 0);
    out.set(encrypted, NONCE_SIZE);
    return bytesToB64(out);
  } catch {
    return null;
  }
};

export const pyEncrypt = async (data) => {
  try {
    const k = await getKey("body", PY_SECRET_KEY);
    const { nonce, encrypted } = await gcmEncryptRaw(k, JSON.stringify(data));
    return bytesToB64(nonce) + ":" + bytesToB64(encrypted);
  } catch {
    return null;
  }
};

export const pyDecrypt = async (input) => {
  try {
    const value = typeof input === "string" ? input : input?.data;
    if (typeof value !== "string") return null;

    const parts = value.split(":");
    if (parts.length !== 2) return null;

    const iv = b64ToBytes(parts[0]);
    const ctWithTag = b64ToBytes(parts[1]);
    if (iv.length !== NONCE_SIZE) return null;

    const k = await getKey("body", PY_SECRET_KEY);
    const plain = await gcmDecryptRaw(k, iv, ctWithTag);
    return JSON.parse(new TextDecoder().decode(plain));
  } catch {
    return null; // bad data / auth tag failed
  }
};